import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { auth, isProduction } from '../firebase';
import { db } from '../lib/firebase';
import { parseSessionPath, subscribeToEvents } from '../lib/raceEvents';

// Injected by vite.config.js — see the `define` block there.
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '';
const BUILD_SHA = typeof __BUILD_SHA__ !== 'undefined' ? __BUILD_SHA__ : '';
const BUILD_TIME = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : '';

const ALL_SOURCES = ['firestore', 'console'];
const ALL_LEVELS = ['debug', 'info', 'warn', 'error'];
const TIME_RANGES = [
  { value: 'all', label: 'All' },
  { value: '15m', label: '15m', ms: 15 * 60 * 1000 },
  { value: '5m', label: '5m', ms: 5 * 60 * 1000 },
  { value: '1m', label: '1m', ms: 60 * 1000 },
];
const MAX_ENTRIES = 2000;

const SOURCE_COLOR = {
  firestore: '#0d6efd',
  console: '#6f42c1',
};
const LEVEL_COLOR = {
  debug: '#6c757d',
  info: '#198754',
  warn: '#fd7e14',
  error: '#dc3545',
};

let entryCounter = 0;
function nextEntryId() {
  entryCounter += 1;
  return `e${entryCounter}_${Date.now().toString(36)}`;
}

function tsToMillis(ts) {
  if (ts == null) return Date.now();
  if (typeof ts === 'object' && typeof ts.toDate === 'function') return ts.toDate().getTime();
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === 'string') {
    const parsed = new Date(ts).getTime();
    return Number.isFinite(parsed) ? parsed : Date.now();
  }
  if (typeof ts === 'number') return ts;
  return Date.now();
}

function formatClock(ms) {
  const d = new Date(ms);
  const pad = (n, w = 2) => String(n).padStart(w, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

function safeStringify(value) {
  try {
    return JSON.stringify(value, (_, v) => {
      if (v && typeof v === 'object' && typeof v.toDate === 'function') {
        return v.toDate().toISOString();
      }
      return v;
    }, 2);
  } catch {
    return String(value);
  }
}

function levelForFirestoreEvent(type) {
  if (type === 'RaceAbandoned') return 'warn';
  return 'info';
}

function summariseFirestoreEvent(ev) {
  const parts = [ev.type || '(untyped)'];
  if (ev.authority) parts.push(`authority=${ev.authority}`);
  if (ev.course_name || ev.courseName) parts.push(`course=${ev.course_name || ev.courseName}`);
  if (ev.from_mark_index != null && ev.to_mark_index != null) {
    parts.push(`mark ${ev.from_mark_index}→${ev.to_mark_index}`);
  } else if (ev.to_mark_index != null) {
    parts.push(`mark→${ev.to_mark_index}`);
  }
  return parts.join(' · ');
}

function CollapsibleSection({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section style={{ marginBottom: '0.75rem', borderBottom: '1px solid var(--gray-light, #dee2e6)' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          background: 'none',
          border: 'none',
          padding: '0.5rem 0',
          cursor: 'pointer',
          fontSize: '0.95rem',
          fontWeight: 600,
          color: 'var(--gray-dark, #343a40)',
          textAlign: 'left',
        }}
      >
        <span style={{ display: 'inline-block', width: '1rem', color: 'var(--gray, #6c757d)' }}>
          {open ? '▾' : '▸'}
        </span>
        {title}
      </button>
      {open && <div style={{ paddingBottom: '0.5rem' }}>{children}</div>}
    </section>
  );
}

function KvTable({ rows }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} style={{ borderBottom: '1px solid var(--gray-light, #f1f3f5)' }}>
            <th
              scope="row"
              style={{
                textAlign: 'left',
                padding: '.3rem .4rem',
                width: '40%',
                fontWeight: 500,
                color: 'var(--gray, #6c757d)',
                verticalAlign: 'top',
              }}
            >
              {k}
            </th>
            <td
              style={{
                padding: '.3rem .4rem',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                wordBreak: 'break-all',
              }}
            >
              {v == null || v === '' ? <span style={{ color: '#999' }}>—</span> : String(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Pill({ text, color }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 6px',
        borderRadius: 3,
        backgroundColor: color,
        color: '#fff',
        fontSize: '.7rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '.02em',
        minWidth: 50,
        textAlign: 'center',
      }}
    >
      {text}
    </span>
  );
}

function ToggleChip({ label, active, color, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '2px 8px',
        borderRadius: 12,
        border: `1px solid ${active ? color : 'var(--gray-light, #dee2e6)'}`,
        background: active ? color : 'transparent',
        color: active ? '#fff' : 'var(--gray-dark, #343a40)',
        fontSize: '.75rem',
        fontWeight: 500,
        cursor: 'pointer',
        textTransform: 'lowercase',
      }}
    >
      {label}
    </button>
  );
}

function ConsoleRow({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const hasPayload = entry.payload !== undefined && entry.payload !== null;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '110px 90px 70px 1fr',
        gap: '0.5rem',
        padding: '4px 8px',
        borderBottom: '1px solid #1f2937',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: '.78rem',
        color: '#e5e7eb',
        cursor: hasPayload ? 'pointer' : 'default',
      }}
      onClick={() => hasPayload && setExpanded((e) => !e)}
    >
      <span style={{ color: '#9ca3af' }}>{formatClock(entry.timestamp)}</span>
      <Pill text={entry.source} color={SOURCE_COLOR[entry.source] || '#6c757d'} />
      <Pill text={entry.level} color={LEVEL_COLOR[entry.level] || '#6c757d'} />
      <div>
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {hasPayload && (
            <span style={{ color: '#9ca3af', marginRight: 4 }}>{expanded ? '▾' : '▸'}</span>
          )}
          {entry.message}
        </div>
        {expanded && hasPayload && (
          <pre
            style={{
              margin: '4px 0 0',
              padding: '6px 8px',
              backgroundColor: '#0b1220',
              border: '1px solid #1f2937',
              borderRadius: 3,
              color: '#cbd5e1',
              fontSize: '.72rem',
              maxHeight: 320,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {safeStringify(entry.payload)}
          </pre>
        )}
      </div>
    </div>
  );
}

export default function AdminDiagnostics() {
  const [params] = useSearchParams();
  const { user, portalRole } = useAuth();
  const [tokenClaims, setTokenClaims] = useState(null);

  const [entries, setEntries] = useState([]);
  const [meta, setMeta] = useState(null);
  const [streamError, setStreamError] = useState(null);
  const seenFirestoreIds = useRef(new Set());

  const [search, setSearch] = useState('');
  const [activeSources, setActiveSources] = useState(() => new Set(ALL_SOURCES));
  const [activeLevels, setActiveLevels] = useState(() => new Set(ALL_LEVELS));
  const [timeRange, setTimeRange] = useState('all');
  const [paused, setPaused] = useState(false);
  const [pinToBottom, setPinToBottom] = useState(true);
  const [pauseSnapshot, setPauseSnapshot] = useState(null);

  const searchInputRef = useRef(null);
  const consoleEndRef = useRef(null);

  const racePath = useMemo(() => parseSessionPath(params.get('r')), [params]);

  useEffect(() => {
    if (!auth.currentUser) return;
    auth.currentUser
      .getIdTokenResult()
      .then((res) => setTokenClaims(res.claims || {}))
      .catch(() => setTokenClaims({}));
  }, []);

  const appendEntry = useCallback((entry) => {
    setEntries((prev) => {
      const next = [...prev, entry];
      if (next.length > MAX_ENTRIES) next.splice(0, next.length - MAX_ENTRIES);
      return next;
    });
  }, []);

  // Capture console.* output while page is mounted.
  useEffect(() => {
    const original = {};
    const methods = ['debug', 'info', 'log', 'warn', 'error'];
    let suppress = false;
    methods.forEach((m) => {
      original[m] = console[m];
      console[m] = (...args) => {
        original[m](...args);
        if (suppress) return;
        suppress = true;
        try {
          const level = m === 'log' ? 'info' : m;
          const message = args
            .map((a) => (typeof a === 'string' ? a : safeStringify(a)))
            .join(' ');
          appendEntry({
            id: nextEntryId(),
            timestamp: Date.now(),
            source: 'console',
            level,
            message: message.length > 400 ? `${message.slice(0, 400)}…` : message,
            payload: args.length > 1 ? args : args[0],
          });
        } finally {
          suppress = false;
        }
      };
    });
    return () => {
      methods.forEach((m) => {
        console[m] = original[m];
      });
    };
  }, [appendEntry]);

  // Subscribe to _meta to find leader device events path.
  useEffect(() => {
    setMeta(null);
    seenFirestoreIds.current = new Set();
    if (!racePath || !racePath.sessionPath) return undefined;
    const metaRef = doc(db, racePath.sessionPath, '_meta');
    const unsub = onSnapshot(
      metaRef,
      (snap) => {
        if (snap.exists()) setMeta(snap.data());
        else setMeta({ _missing: true });
      },
      (err) => setStreamError(err.message),
    );
    return unsub;
  }, [racePath]);

  // Subscribe to deviceEvents for the leader device.
  useEffect(() => {
    if (!meta?.leaderDevicePath) return undefined;
    return subscribeToEvents(
      meta.leaderDevicePath,
      (events) => {
        for (const ev of events) {
          if (seenFirestoreIds.current.has(ev.id)) continue;
          seenFirestoreIds.current.add(ev.id);
          appendEntry({
            id: `fs_${ev.id}`,
            timestamp: tsToMillis(ev.timestamp),
            source: 'firestore',
            level: levelForFirestoreEvent(ev.type),
            message: summariseFirestoreEvent(ev),
            payload: ev,
          });
        }
      },
      (err) => setStreamError(err.message),
    );
  }, [meta?.leaderDevicePath, appendEntry]);

  // Keyboard shortcuts: Cmd+K focus search, Cmd+L clear log.
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        searchInputRef.current?.focus();
      } else if (e.key === 'l' || e.key === 'L') {
        e.preventDefault();
        setEntries([]);
        seenFirestoreIds.current = new Set();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Snapshot the rendered list when paused; release on resume.
  useEffect(() => {
    setPauseSnapshot((prev) => (paused ? (prev ?? entries) : null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  const visible = useMemo(() => {
    const base = paused && pauseSnapshot ? pauseSnapshot : entries;
    const now = Date.now();
    const range = TIME_RANGES.find((r) => r.value === timeRange);
    const cutoff = range && range.ms ? now - range.ms : null;
    const needle = search.trim().toLowerCase();
    return base.filter((e) => {
      if (!activeSources.has(e.source)) return false;
      if (!activeLevels.has(e.level)) return false;
      if (cutoff != null && e.timestamp < cutoff) return false;
      if (needle) {
        const hay = `${formatClock(e.timestamp)} ${e.source} ${e.level} ${e.message}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [entries, pauseSnapshot, paused, activeSources, activeLevels, timeRange, search]);

  useEffect(() => {
    if (!pinToBottom || paused) return;
    consoleEndRef.current?.scrollIntoView({ block: 'end' });
  }, [visible.length, pinToBottom, paused]);

  const toggleSet = (setter, value) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const clearLog = () => {
    setEntries([]);
    setPauseSnapshot(null);
    seenFirestoreIds.current = new Set();
  };

  const buildRows = [
    ['App version', APP_VERSION],
    ['Build SHA', BUILD_SHA ? BUILD_SHA.slice(0, 12) : ''],
    ['Build time', BUILD_TIME],
    ['Environment', isProduction ? 'production (racemarksapp)' : 'staging/dev'],
    ['Firebase project', auth.app.options.projectId],
    ['Auth domain', auth.app.options.authDomain],
    ['Page loaded', new Date().toISOString()],
    ['User agent', typeof navigator !== 'undefined' ? navigator.userAgent : ''],
  ];

  const sessionRows = [
    ['UID', user?.uid],
    ['Email', user?.email],
    ['Email verified', user ? String(user.emailVerified) : ''],
    ['Provider', user?.providerData?.[0]?.providerId],
    ['Portal role', portalRole?.role],
    ['Portal clubId', portalRole?.clubId],
    ['Claim: admin', tokenClaims?.admin ?? ''],
    ['Claim: portalRole', tokenClaims?.portalRole ?? ''],
    ['Claim: portalClubId', tokenClaims?.portalClubId ?? ''],
    ['Claim: betaTier', tokenClaims?.betaTier ?? ''],
  ];

  const raceRows = racePath
    ? [
        ['Country', racePath.country],
        ['Club', racePath.club],
        ['Date', racePath.date],
        ['Fleet/class', racePath.fleetClass],
        ['Vessel ID', racePath.vesselId],
        ['Session token', racePath.sessionToken],
        ['Raw path', racePath.sessionPath],
        ['Leader device', meta?.leaderDevicePath || (meta?._missing ? '(no _meta doc)' : '…')],
      ]
    : null;

  return (
    <>
      <PortalHeader />
      <div className="page" style={{ maxWidth: 'none', padding: '0.75rem 1rem 1.5rem' }}>
        <PageHeader backTo="/admin" title="Diagnostics" />

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            alignItems: 'center',
            padding: '0.5rem 0.75rem',
            backgroundColor: 'var(--gray-light, #f1f3f5)',
            borderRadius: 4,
            marginBottom: '0.5rem',
            fontSize: '.85rem',
          }}
        >
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Filter (⌘K)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: '1 1 200px',
              minWidth: 160,
              padding: '4px 8px',
              border: '1px solid var(--gray-light, #dee2e6)',
              borderRadius: 3,
              fontSize: '.85rem',
              fontFamily: 'inherit',
            }}
          />
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {ALL_SOURCES.map((s) => (
              <ToggleChip
                key={s}
                label={s}
                active={activeSources.has(s)}
                color={SOURCE_COLOR[s]}
                onClick={() => toggleSet(setActiveSources, s)}
              />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {ALL_LEVELS.map((l) => (
              <ToggleChip
                key={l}
                label={l}
                active={activeLevels.has(l)}
                color={LEVEL_COLOR[l]}
                onClick={() => toggleSet(setActiveLevels, l)}
              />
            ))}
          </div>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{ padding: '3px 6px', fontSize: '.85rem' }}
            aria-label="Time range"
          >
            {TIME_RANGES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setPaused((p) => !p)}
            style={{
              padding: '4px 10px',
              fontSize: '.8rem',
              border: '1px solid var(--gray-light, #dee2e6)',
              borderRadius: 3,
              background: paused ? '#fd7e14' : 'transparent',
              color: paused ? '#fff' : 'inherit',
              cursor: 'pointer',
            }}
          >
            {paused ? 'Resume' : 'Pause'}
          </button>
          <button
            type="button"
            onClick={clearLog}
            style={{
              padding: '4px 10px',
              fontSize: '.8rem',
              border: '1px solid var(--gray-light, #dee2e6)',
              borderRadius: 3,
              background: 'transparent',
              cursor: 'pointer',
            }}
            title="Clear (⌘L)"
          >
            Clear
          </button>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: '.8rem',
              color: 'var(--gray-dark, #343a40)',
            }}
          >
            <input
              type="checkbox"
              checked={pinToBottom}
              onChange={(e) => setPinToBottom(e.target.checked)}
            />
            Pin to bottom
          </label>
          <span style={{ marginLeft: 'auto', fontSize: '.75rem', color: 'var(--gray, #6c757d)' }}>
            {visible.length} / {entries.length}
          </span>
        </div>

        <div
          className="diag-layout"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) 320px',
            gap: '0.75rem',
            alignItems: 'stretch',
          }}
        >
          <div
            style={{
              backgroundColor: '#111827',
              border: '1px solid #1f2937',
              borderRadius: 4,
              minHeight: 480,
              maxHeight: 'calc(100vh - 220px)',
              overflowY: 'auto',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            }}
          >
            {streamError && (
              <div
                style={{
                  padding: '6px 10px',
                  color: '#fca5a5',
                  borderBottom: '1px solid #1f2937',
                  fontSize: '.78rem',
                }}
              >
                Stream error: {streamError}
              </div>
            )}
            {visible.length === 0 ? (
              <div style={{ padding: '1rem', color: '#9ca3af', fontSize: '.85rem' }}>
                {racePath
                  ? 'Waiting for events…'
                  : 'No race context. Append ?r=<encoded-session-path> to stream live device events.'}
              </div>
            ) : (
              visible.map((e) => <ConsoleRow key={e.id} entry={e} />)
            )}
            <div ref={consoleEndRef} />
          </div>

          <aside
            style={{
              backgroundColor: 'var(--white, #fff)',
              border: '1px solid var(--gray-light, #dee2e6)',
              borderRadius: 4,
              padding: '0.5rem 0.75rem',
              fontSize: '.85rem',
              maxHeight: 'calc(100vh - 220px)',
              overflowY: 'auto',
            }}
          >
            <CollapsibleSection title="Build">
              <KvTable rows={buildRows} />
            </CollapsibleSection>

            <CollapsibleSection title="Session">
              <KvTable rows={sessionRows} />
            </CollapsibleSection>

            {raceRows && (
              <CollapsibleSection title="Race context (?r=)">
                <KvTable rows={raceRows} />
              </CollapsibleSection>
            )}

            <CollapsibleSection title="Notes" defaultOpen={false}>
              <ul
                style={{
                  fontSize: '.8rem',
                  color: 'var(--gray-dark, #343a40)',
                  paddingLeft: '1rem',
                  margin: 0,
                }}
              >
                <li>
                  Live stream covers Firestore <code>deviceEvents</code> for the leader device of
                  the race-context session, plus browser <code>console.*</code> output captured
                  while this page is open.
                </li>
                <li>
                  Logger-bus / NavEvent / RaceTimer / NMEA streams are not yet wired in the
                  committee portal — follow-up issue to add them as further sources.
                </li>
                <li>Buffer capped at {MAX_ENTRIES} entries; oldest are dropped first.</li>
              </ul>
            </CollapsibleSection>
          </aside>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .diag-layout {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
}
